import { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api, getToken } from '@shared/services/api';
import {
  AreaChart,
  Area,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { AgentMetricsClient, AgentMetricsMessage } from '@shared/lib/websocket';
import AgentCommunicationLogsViewer from '@features/agents/components/AgentCommunicationLogsViewer';
import { useDialog } from '@shared/hooks/useDialog';
import { useTheme } from '@shared/contexts/ThemeContext';
import { getStatusTone } from '@shared/config/theme';
import { vizColor } from '@shared/utils/statusTone';
import { StatusChip } from '@shared/components/StatusChip';
import { useBreadcrumbLabel } from '@shared/contexts/BreadcrumbLabelContext';
import { Card, Button } from '@shared/components/ui';
import {
  Monitor,
  Activity,
  Database,
  Shield,
  RefreshCw,
  ArrowLeft,
  Zap,
  Clock,
  History,
  AlertTriangle,
  MessageSquare,
  Plus,
  Key,
  Copy,
  ShieldCheck,
  RotateCcw,
} from 'lucide-react';

interface Agent {
  id: string;
  name: string;
  status: string;
  version?: string;
  os_version?: string;
  ip_address?: string;
  last_heartbeat?: string;
  first_connected_at?: string;
  auto_update_enabled?: boolean;
  last_update_check?: string;
  latest_available_version?: string;
  update_available?: boolean;
  installer_expires_at?: string;
  installer_downloaded_at?: string;
  created_at: string;
  updated_at: string;
}

interface AgentMetrics {
  agent_id: string;
  cpu_metrics: Array<{ timestamp: string; usage: number }>;
  current_cpu_usage: number | null;
  memory_metrics: Array<{ timestamp: string; percent: number; total: number; used: number }>;
  current_memory_percent: number | null;
  current_memory_total: number;
  current_memory_used: number;
  disk_metrics: Array<{ timestamp: string; percent: number; total: number; used: number }>;
  current_disk_percent: number | null;
  current_disk_total: number;
  current_disk_used: number;
  io_metrics: Array<{
    timestamp: string;
    read_mb_s: number;
    write_mb_s: number;
    iops: number;
    latency_ms: number;
  }>;
  current_io_read_mb_s: number | null;
  current_io_write_mb_s: number | null;
  current_io_iops: number | null;
  current_io_latency_ms: number | null;
  hours: number;
}

export default function AgentDetails() {
  const { t, i18n } = useTranslation('agents');
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { showError, showSuccess, showConfirm } = useDialog();
  const { theme } = useTheme();
  const [agent, setAgent] = useState<Agent | null>(null);
  useBreadcrumbLabel(agent && agent.id === id ? agent.name : null);
  const [installation, setInstallation] = useState<{
    instance_name: string;
    install_dir: string;
    service_name: string;
    health_port: number;
  } | null>(null);
  const chartContainerRef = useRef<HTMLDivElement>(null);
  const [chartInitialDimension, setChartInitialDimension] = useState<{
    width: number;
    height: number;
  }>();
  useEffect(() => {
    if (!id) return;
    let active = true;
    api
      .get(`/agents/${id}/installation`)
      .then((response) => {
        if (active) setInstallation(response.data);
      })
      .catch(() => {
        if (active) setInstallation(null);
      });
    return () => {
      active = false;
    };
  }, [id]);
  const [metrics, setMetrics] = useState<AgentMetrics | null>(null);
  const [metricsLoading, setMetricsLoading] = useState(true);
  const [metricsError, setMetricsError] = useState('');
  const [timePeriod, setTimePeriod] = useState('24');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [liveMetricsEnabled, setLiveMetricsEnabled] = useState(false);
  const [wsClient, setWsClient] = useState<AgentMetricsClient | null>(null);
  const [showCommunicationLogs, setShowCommunicationLogs] = useState(false);
  const [regeneratedToken, setRegeneratedToken] = useState<{
    token: string;
    agentName: string;
  } | null>(null);

  useEffect(() => {
    if (id) {
      fetchAgentDetails(id);
      fetchAgentMetrics(id);
    }
  }, [id, timePeriod]);

  useLayoutEffect(() => {
    if (!metrics) {
      setChartInitialDimension(undefined);
      return;
    }

    const { width, height } = chartContainerRef.current?.getBoundingClientRect() ?? {
      width: 0,
      height: 0,
    };
    if (width <= 0 || height <= 0) return;

    const nextDimension = { width: Math.round(width), height: Math.round(height) };
    setChartInitialDimension((current) =>
      current?.width === nextDimension.width && current.height === nextDimension.height
        ? current
        : nextDimension
    );
  }, [metrics]);

  useEffect(() => {
    if (!id || !liveMetricsEnabled) {
      if (wsClient) {
        wsClient.disconnect();
        setWsClient(null);
      }
      return;
    }

    const token = getToken();
    if (!token) return;

    const client = new AgentMetricsClient(
      id,
      token,
      (message: AgentMetricsMessage) => {
        setMetrics((prev) => {
          if (!prev) return prev;
          const now = new Date(message.timestamp * 1000).toISOString();
          const maxPoints = 50;

          return {
            ...prev,
            cpu_metrics: [...prev.cpu_metrics, { timestamp: now, usage: message.cpu }].slice(
              -maxPoints
            ),
            memory_metrics: [
              ...prev.memory_metrics,
              {
                timestamp: now,
                percent: message.memory,
                total: prev.current_memory_total,
                used: Math.floor((prev.current_memory_total * message.memory) / 100),
              },
            ].slice(-maxPoints),
            disk_metrics: [
              ...prev.disk_metrics,
              {
                timestamp: now,
                percent: message.disk,
                total: prev.current_disk_total,
                used: Math.floor((prev.current_disk_total * message.disk) / 100),
              },
            ].slice(-maxPoints),
            io_metrics: [
              ...prev.io_metrics,
              {
                timestamp: now,
                read_mb_s: message.io_read,
                write_mb_s: message.io_write,
                iops: message.iops,
                latency_ms: message.latency,
              },
            ].slice(-maxPoints),
            current_cpu_usage: message.cpu,
            current_memory_percent: message.memory,
            current_disk_percent: message.disk,
            current_io_read_mb_s: message.io_read,
            current_io_write_mb_s: message.io_write,
            current_io_iops: message.iops,
            current_io_latency_ms: message.latency,
          };
        });
      },
      (err: string) => {
        console.error('WebSocket error:', err);
        setLiveMetricsEnabled(false);
      }
    );

    client.connect();
    setWsClient(client);
    return () => {
      client.disconnect();
    };
  }, [id, liveMetricsEnabled]);

  const fetchAgentDetails = async (agentId: string) => {
    try {
      const response = await api.get<Agent>(`/agents/${agentId}`);
      setAgent(response.data);
    } catch {
      setError(t('details.errors.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  const fetchAgentMetrics = async (agentId: string) => {
    setMetricsLoading(true);
    setMetricsError('');
    try {
      const response = await api.get<AgentMetrics>(
        `/agents/${agentId}/metrics?hours=${timePeriod}`
      );
      setMetrics(response.data);
    } catch {
      setMetrics(null);
      setMetricsError(t('details.telemetry.loadFailed'));
    } finally {
      setMetricsLoading(false);
    }
  };

  if (loading) {
    return (
      <>
        <div className="py-20 flex justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-600"></div>
        </div>
      </>
    );
  }

  if (error || !agent) {
    return (
      <>
        <Card className="py-20 text-center text-state-danger-light dark:text-state-danger-dark font-bold border-state-danger-light/20 bg-state-danger-bg-light/50 dark:bg-state-danger-bg-dark">
          {error || t('details.errors.notFound')}
          <Button
            variant="outline"
            className="mt-4 block mx-auto"
            onClick={() => navigate('/agents')}
          >
            {t('details.actions.backToFleet')}
          </Button>
        </Card>
      </>
    );
  }

  // Series colors — same source as the chart strokes below (viz-1..4).
  const cpuColor = vizColor('cpu', theme);
  const ramColor = vizColor('ram', theme);
  const diskColor = vizColor('disk', theme);
  const ioColor = vizColor('io', theme);

  return (
    <>
      <div className="space-y-8 animate-in fade-in duration-700">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/agents')}
              aria-label={t('details.actions.backToFleet')}
              className="p-2 hover:bg-surface-2-light dark:hover:bg-surface-2-dark rounded-xl"
            >
              <ArrowLeft size={20} className="text-text-subtle-light dark:text-text-subtle-dark" />
            </Button>
            <div>
              <div className="flex items-center gap-3">
                <h1 className="text-3xl font-black text-slate-900 dark:text-white tracking-tight uppercase">
                  {agent.name}
                </h1>
                <StatusChip
                  tone={getStatusTone(agent.status)}
                  label={t(`status.${agent.status.toLowerCase()}`)}
                  size="md"
                />
              </div>
              <p className="text-xs font-bold text-text-subtle-light dark:text-text-subtle-dark font-mono mt-1">
                {agent.id}
              </p>
            </div>
          </div>
          <div className="flex flex-col gap-2 sm:flex-row">
            <Button
              className="w-full sm:w-auto"
              variant="secondary"
              onClick={() => setShowCommunicationLogs(true)}
            >
              <MessageSquare size={18} className="mr-2" /> {t('details.actions.logs')}
            </Button>
            <Button
              className="w-full sm:w-auto"
              variant="secondary"
              onClick={() => navigate('/backups/configs')}
            >
              <Database size={18} className="mr-2" /> {t('details.actions.configs')}
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Detailed Info */}
          <div className="lg:col-span-2 space-y-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card className="p-6">
                <h3 className="text-sm font-black uppercase tracking-widest text-text-subtle-light dark:text-text-subtle-dark mb-6 flex items-center gap-2">
                  <Monitor size={14} className="text-brand-600 dark:text-brand-400" />{' '}
                  {t('details.sections.platformInfo')}
                </h3>
                <div className="space-y-4">
                  <DetailItem
                    label={t('details.labels.osVersion')}
                    value={agent.os_version || t('labels.na')}
                  />
                  <DetailItem
                    label={t('details.labels.agentVersion')}
                    value={agent.version || t('labels.na')}
                  />
                  {installation && (
                    <>
                      <DetailItem
                        label={t('details.labels.instance')}
                        value={installation.instance_name || t('details.labels.legacy')}
                      />
                      <DetailItem
                        label={t('details.labels.installDirectory')}
                        value={installation.install_dir}
                        mono
                      />
                      <DetailItem
                        label={t('details.labels.windowsService')}
                        value={installation.service_name}
                        mono
                      />
                      <DetailItem
                        label={t('details.labels.localPort')}
                        value={String(installation.health_port)}
                      />
                    </>
                  )}
                  <DetailItem
                    label={t('details.labels.ipAddress')}
                    value={agent.ip_address || t('labels.na')}
                    mono
                  />
                </div>
              </Card>

              <Card className="p-6">
                <h3 className="text-sm font-black uppercase tracking-widest text-text-subtle-light dark:text-text-subtle-dark mb-6 flex items-center gap-2">
                  <Clock size={14} className="text-state-info-light dark:text-state-info-dark" />{' '}
                  {t('details.sections.connectivity')}
                </h3>
                <div className="space-y-4">
                  <DetailItem
                    label={t('details.labels.lastHeartbeat')}
                    value={
                      agent.last_heartbeat
                        ? new Date(agent.last_heartbeat).toLocaleString(i18n.resolvedLanguage)
                        : t('details.labels.never')
                    }
                  />
                  <DetailItem
                    label={t('details.labels.firstConnected')}
                    value={
                      agent.first_connected_at
                        ? new Date(agent.first_connected_at).toLocaleString(i18n.resolvedLanguage)
                        : t('labels.na')
                    }
                  />
                  <DetailItem
                    label={t('details.labels.pairingDate')}
                    value={new Date(agent.created_at).toLocaleDateString(i18n.resolvedLanguage)}
                  />
                </div>
              </Card>
            </div>

            {/* Metrics Chart Overhaul */}
            <Card className="p-6 overflow-hidden">
              <div className="flex flex-col gap-4 mb-8 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <h3 className="text-lg font-black text-slate-900 dark:text-white flex items-center gap-2">
                    <Activity size={20} className="text-brand-600 dark:text-brand-400" />{' '}
                    {t('details.sections.resourceTelemetry')}
                  </h3>
                  <p className="text-xs font-bold text-text-subtle-light dark:text-text-subtle-dark uppercase tracking-widest mt-1">
                    {t('details.telemetry.subtitle')}
                  </p>
                </div>
                <div className="flex flex-wrap items-center gap-3">
                  <Button
                    size="sm"
                    variant={liveMetricsEnabled ? 'primary' : 'outline'}
                    onClick={() => setLiveMetricsEnabled(!liveMetricsEnabled)}
                    className={`min-h-11 shrink-0 whitespace-nowrap ${liveMetricsEnabled ? 'shadow-brand-200' : ''}`}
                  >
                    {liveMetricsEnabled && (
                      <span className="w-2 h-2 mr-2 bg-white rounded-full animate-pulse" />
                    )}
                    {liveMetricsEnabled
                      ? t('details.telemetry.liveOn')
                      : t('details.telemetry.goLive')}
                  </Button>
                  <select
                    aria-label={t('details.telemetry.period')}
                    value={timePeriod}
                    onChange={(e) => setTimePeriod(e.target.value)}
                    className="app-field-control min-h-11 w-auto min-w-24"
                  >
                    <option value="1">1h</option>
                    <option value="6">6h</option>
                    <option value="24">24h</option>
                    <option value="168">7d</option>
                  </select>
                </div>
              </div>

              {metricsLoading && !liveMetricsEnabled ? (
                <div className="h-64 flex items-center justify-center">
                  <div className="animate-spin h-8 w-8 border-b-2 border-brand-600 rounded-full"></div>
                </div>
              ) : metricsError ? (
                <div
                  role="alert"
                  className="h-64 flex flex-col items-center justify-center gap-3 text-center"
                >
                  <p className="text-sm font-semibold text-state-danger-light dark:text-state-danger-dark">
                    {metricsError}
                  </p>
                  <Button
                    variant="outline"
                    className="min-h-11"
                    disabled={!id}
                    onClick={() => {
                      if (id) void fetchAgentMetrics(id);
                    }}
                  >
                    {t('details.telemetry.retry')}
                  </Button>
                </div>
              ) : metrics ? (
                <div className="space-y-8">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <StatBox
                      label={t('details.metrics.cpuLabel')}
                      value={`${metrics.current_cpu_usage?.toFixed(1) ?? '0.0'}%`}
                      color="text-viz-1-light dark:text-viz-1-dark"
                    />
                    <StatBox
                      label={t('details.metrics.ramLabel')}
                      value={`${metrics.current_memory_percent?.toFixed(1) ?? '0.0'}%`}
                      color="text-viz-2-light dark:text-viz-2-dark"
                    />
                    <StatBox
                      label={t('details.metrics.diskLabel')}
                      value={`${metrics.current_disk_percent?.toFixed(1) ?? '0.0'}%`}
                      color="text-viz-3-light dark:text-viz-3-dark"
                    />
                    <StatBox
                      label={t('details.metrics.ioLabel')}
                      value={`${((metrics.current_io_read_mb_s ?? 0) + (metrics.current_io_write_mb_s ?? 0)).toFixed(1)} MB/s`}
                      color="text-viz-4-light dark:text-viz-4-dark"
                    />
                  </div>

                  <div ref={chartContainerRef} className="h-64 w-full min-w-0">
                    {chartInitialDimension && (
                      <ResponsiveContainer
                        width="100%"
                        height="100%"
                        initialDimension={chartInitialDimension}
                      >
                        <AreaChart
                          data={(() => {
                            // Aggregate all metrics by 10-second intervals
                            const get10SecBucket = (ts: string) => {
                              const d = new Date(ts);
                              // Round to nearest 10 seconds using milliseconds (timezone-agnostic)
                              const ms = d.getTime();
                              return (Math.floor(ms / 10000) * 10000).toString();
                            };
                            const secGroups = new Map<
                              string,
                              {
                                cpu: number[];
                                memory: number[];
                                disk: number[];
                                io: number[];
                                label: string;
                              }
                            >();
                            // Helper to ensure group exists
                            const getGroup = (bucket: string) => {
                              if (!secGroups.has(bucket)) {
                                const d = new Date(bucket);
                                const timeLabel =
                                  Number(timePeriod) <= 24
                                    ? d.toLocaleTimeString(i18n.resolvedLanguage, {
                                        hour: '2-digit',
                                        minute: '2-digit',
                                        second: '2-digit',
                                        hour12: false,
                                      })
                                    : d.toLocaleString(i18n.resolvedLanguage, {
                                        month: 'short',
                                        day: 'numeric',
                                        hour: '2-digit',
                                        minute: '2-digit',
                                        hour12: false,
                                      });
                                secGroups.set(bucket, {
                                  cpu: [],
                                  memory: [],
                                  disk: [],
                                  io: [],
                                  label: timeLabel,
                                });
                              }
                              return secGroups.get(bucket)!;
                            };
                            // Group CPU by 10-second bucket
                            metrics.cpu_metrics.forEach((m) => {
                              const bucket = get10SecBucket(m.timestamp);
                              getGroup(bucket).cpu.push(m.usage);
                            });
                            // Group Memory by 10-second bucket
                            metrics.memory_metrics.forEach((m) => {
                              const bucket = get10SecBucket(m.timestamp);
                              getGroup(bucket).memory.push(m.percent);
                            });
                            // Group Disk by 10-second bucket
                            metrics.disk_metrics.forEach((m) => {
                              const bucket = get10SecBucket(m.timestamp);
                              getGroup(bucket).disk.push(m.percent);
                            });
                            // Group I/O by 10-second bucket
                            metrics.io_metrics.forEach((m) => {
                              const bucket = get10SecBucket(m.timestamp);
                              getGroup(bucket).io.push((m.read_mb_s ?? 0) + (m.write_mb_s ?? 0));
                            });
                            // Calculate averages and sort by timestamp (numeric sort since keys are ms)
                            return Array.from(secGroups.entries())
                              .sort((a, b) => Number(a[0]) - Number(b[0]))
                              .map(([bucket, g]) => ({
                                time: Number(bucket),
                                cpu: g.cpu.length
                                  ? Math.round(
                                      (g.cpu.reduce((a, b) => a + b, 0) / g.cpu.length) * 100
                                    ) / 100
                                  : 0,
                                memory: g.memory.length
                                  ? Math.round(
                                      (g.memory.reduce((a, b) => a + b, 0) / g.memory.length) * 100
                                    ) / 100
                                  : 0,
                                disk: g.disk.length
                                  ? Math.round(
                                      (g.disk.reduce((a, b) => a + b, 0) / g.disk.length) * 100
                                    ) / 100
                                  : 0,
                                io: g.io.length
                                  ? Math.round(
                                      (g.io.reduce((a, b) => a + b, 0) / g.io.length) * 10 * 100
                                    ) / 100
                                  : 0,
                              }));
                          })()}
                        >
                          <defs>
                            <linearGradient id="colorCpu" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor={cpuColor} stopOpacity={0.1} />
                              <stop offset="95%" stopColor={cpuColor} stopOpacity={0} />
                            </linearGradient>
                            <linearGradient id="colorMemory" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor={ramColor} stopOpacity={0.1} />
                              <stop offset="95%" stopColor={ramColor} stopOpacity={0} />
                            </linearGradient>
                            <linearGradient id="colorDisk" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor={diskColor} stopOpacity={0.1} />
                              <stop offset="95%" stopColor={diskColor} stopOpacity={0} />
                            </linearGradient>
                          </defs>
                          <CartesianGrid
                            strokeDasharray="3 3"
                            vertical={false}
                            stroke="currentColor"
                            className="text-surface-border-light dark:text-surface-border-dark"
                          />
                          <XAxis dataKey="time" hide />
                          <YAxis hide yAxisId="left" domain={[0, 100]} />
                          <YAxis hide yAxisId="right" orientation="right" domain={[0, 'auto']} />
                          <Legend
                            verticalAlign="top"
                            height={20}
                            iconType="circle"
                            wrapperStyle={{ fontSize: '11px', paddingBottom: '10px' }}
                          />
                          <Tooltip
                            contentStyle={{
                              backgroundColor: 'var(--tooltip-bg)',
                              border: 'none',
                              borderRadius: '8px',
                              color: 'var(--tooltip-text)',
                              fontSize: '12px',
                            }}
                            labelFormatter={
                              ((label: string | number) => {
                                const timestamp = typeof label === 'string' ? Number(label) : label;
                                if (isNaN(timestamp)) return String(label);
                                // Show date + time for all periods (useful for multi-day views)
                                return new Date(timestamp).toLocaleString(i18n.resolvedLanguage, {
                                  month: 'short',
                                  day: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit',
                                  second: '2-digit',
                                  hour12: false,
                                });
                              }) as any
                            }
                            formatter={
                              ((value: number | undefined, name: string | undefined) => {
                                const label = name ?? '';
                                if (value === undefined) return ['N/A', label];
                                if (label === 'IO MB/s')
                                  return [`${(value / 10).toFixed(1)} MB/s`, label];
                                return [`${value.toFixed(1)}%`, label];
                              }) as any
                            }
                          />
                          <Area
                            type="monotone"
                            dataKey="cpu"
                            name={t('details.metrics.cpu')}
                            stroke={cpuColor}
                            fillOpacity={1}
                            fill="url(#colorCpu)"
                            strokeWidth={2}
                            yAxisId="left"
                          />
                          <Area
                            type="monotone"
                            dataKey="memory"
                            name={t('details.metrics.ram')}
                            stroke={ramColor}
                            fillOpacity={1}
                            fill="url(#colorMemory)"
                            strokeWidth={2}
                            yAxisId="left"
                          />
                          <Area
                            type="monotone"
                            dataKey="disk"
                            name={t('details.metrics.disk')}
                            stroke={diskColor}
                            fillOpacity={1}
                            fill="url(#colorDisk)"
                            strokeWidth={2}
                            yAxisId="left"
                          />
                          <Line
                            type="monotone"
                            dataKey="io"
                            name={t('details.metrics.io')}
                            stroke={ioColor}
                            strokeWidth={2}
                            dot={false}
                            yAxisId="right"
                          />
                        </AreaChart>
                      </ResponsiveContainer>
                    )}
                  </div>
                </div>
              ) : (
                <div className="h-64 flex flex-col items-center justify-center text-text-subtle-light dark:text-text-subtle-dark">
                  <History size={40} className="mb-2 opacity-20" />
                  <p className="text-sm font-bold uppercase tracking-widest">
                    {t('details.telemetry.noData')}
                  </p>
                </div>
              )}
            </Card>
          </div>

          {/* Sidebar Controls */}
          <div className="space-y-8">
            <Card className="p-6 bg-surface-card-light dark:bg-surface-card-dark overflow-hidden relative">
              <Zap
                size={120}
                className="absolute -bottom-8 -right-8 text-text-subtle-light dark:text-white opacity-5 rotate-12"
              />
              <h3 className="text-sm font-black uppercase tracking-[0.2em] text-text-muted-light dark:text-text-muted-dark mb-6 relative z-10">
                {t('details.sections.maintenance')}
              </h3>
              <div className="space-y-3 relative z-10">
                <Link
                  to={`/backups/configs?agent=${encodeURIComponent(agent.id)}`}
                  state={{ openCreateWizard: true, backupConfigAgentId: agent.id }}
                  className="inline-flex min-h-11 w-full items-center justify-start gap-2 rounded-lg border-2 border-border-strong-light bg-transparent px-4 py-2.5 text-sm font-semibold text-slate-700 transition-colors hover:border-brand-600 hover:text-brand-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 focus-visible:ring-offset-surface-card-light dark:border-border-strong-dark dark:text-slate-200 dark:focus-visible:ring-brand-400 dark:focus-visible:ring-offset-surface-card-dark sm:w-auto"
                >
                  <Plus size={16} /> {t('details.maintenance.newBackupConfig')}
                </Link>
                <Button
                  variant="outline"
                  className="w-full justify-start border-surface-border-light hover:border-brand-600 hover:text-brand-600"
                  onClick={async () => {
                    try {
                      const res = await api.post(`/agents/${agent?.id}/force-update-check`);
                      if (res.data.success) {
                        await showSuccess(
                          t('details.maintenance.updateCheckSent'),
                          t('details.maintenance.updateCheckSentDesc')
                        );
                      }
                    } catch (err: any) {
                      const errorMsg =
                        err.response?.data?.error || t('details.errors.updateCheckFailed');
                      await showError('Error', errorMsg);
                    }
                  }}
                >
                  <RefreshCw size={16} className="mr-2" />{' '}
                  {t('details.maintenance.forceUpdateCheck')}
                </Button>
                {(agent.status === 'offline' || agent.status === 'revoked') && (
                  <Button
                    variant="outline"
                    className="w-full justify-start border-surface-border-light hover:border-brand-600 hover:text-brand-600"
                    onClick={async () => {
                      if (!agent) return;
                      const confirmed = await showConfirm(
                        t('replaceComputer.confirmTitle'),
                        t('replaceComputer.confirmMessage', { name: agent.name }),
                        'info'
                      );
                      if (!confirmed) return;

                      try {
                        const res = await api.post(`/agents/${agent.id}/regenerate-installer`);
                        const url = res.data.download_url;
                        window.location.href = url;
                        await showSuccess(
                          t('replaceComputer.successTitle'),
                          t('replaceComputer.successMessage', { name: agent.name })
                        );
                        fetchAgentDetails(agent.id);
                      } catch {
                        await showError(
                          t('replaceComputer.errorTitle'),
                          t('replaceComputer.errorMessage')
                        );
                      }
                    }}
                  >
                    <Monitor size={16} className="mr-2" /> {t('replaceComputer.button')}
                  </Button>
                )}
                {agent.status !== 'revoked' && (
                  <Button
                    variant="outline"
                    className="w-full justify-start border-surface-border-light hover:border-brand-600 hover:text-brand-600"
                    onClick={async () => {
                      if (!agent) return;
                      const confirmed = await showConfirm(
                        t('regenerate.confirmTitle'),
                        t('regenerate.confirmMessage', { name: agent.name }),
                        'warning'
                      );
                      if (!confirmed) return;

                      try {
                        const res = await api.post(`/agents/${agent.id}/regenerate-token`);
                        setRegeneratedToken({ token: res.data.token, agentName: agent.name });
                        fetchAgentDetails(agent.id);
                      } catch (err: any) {
                        const errorMsg = err.response?.data?.error || t('regenerate.errorMessage');
                        await showError(t('regenerate.errorTitle'), errorMsg);
                      }
                    }}
                  >
                    <Key size={16} className="mr-2" /> {t('regenerate.button')}
                  </Button>
                )}
                {agent.status === 'revoked' ? (
                  <Button
                    variant="outline"
                    className="w-full justify-start border-state-ok-light/20 text-state-ok-light hover:border-state-ok-light hover:bg-state-ok-bg-light dark:text-state-ok-dark dark:hover:bg-state-ok-bg-dark dark:hover:border-state-ok-dark"
                    onClick={async () => {
                      if (!agent) return;
                      const confirmed = await showConfirm(
                        t('restore.confirmTitle'),
                        t('restore.confirmMessage', { name: agent.name }),
                        'info'
                      );
                      if (!confirmed) return;

                      try {
                        await api.post(`/agents/${agent.id}/restore`);
                        await showSuccess(
                          t('restore.successTitle'),
                          t('restore.successMessage', { name: agent.name })
                        );
                        fetchAgentDetails(agent.id);
                      } catch (err: any) {
                        const errorMsg = err.response?.data?.error || t('restore.errorMessage');
                        await showError(t('restore.errorTitle'), errorMsg);
                      }
                    }}
                  >
                    <RotateCcw size={16} className="mr-2" /> {t('restore.confirmTitle')}
                  </Button>
                ) : (
                  <Button
                    variant="outline"
                    className="w-full justify-start border-state-danger-light/20 text-state-danger-light hover:border-state-danger-light hover:bg-state-danger-bg-light dark:text-state-danger-dark dark:hover:bg-state-danger-bg-dark dark:hover:border-state-danger-dark"
                    onClick={async () => {
                      if (!agent) return;
                      const confirmed = await showConfirm(
                        t('revoke.confirmTitle'),
                        t('revoke.confirmMessage', { name: agent.name }),
                        'warning'
                      );
                      if (!confirmed) return;

                      try {
                        await api.post(`/agents/${agent.id}/revoke`);
                        await showSuccess(
                          t('revoke.successTitle'),
                          t('revoke.successMessage', { name: agent.name })
                        );
                        fetchAgentDetails(agent.id);
                      } catch (err: any) {
                        const errorMsg = err.response?.data?.error || t('revoke.errorMessage');
                        await showError(t('revoke.errorTitle'), errorMsg);
                      }
                    }}
                  >
                    <Shield size={16} className="mr-2" /> {t('details.maintenance.revokeToken')}
                  </Button>
                )}
              </div>
            </Card>

            <Card className="p-6 border-l-4 border-l-state-warn-light dark:border-l-state-warn-dark">
              <h3 className="text-sm font-black uppercase tracking-widest text-slate-900 dark:text-slate-100 mb-4 flex items-center gap-2">
                <AlertTriangle
                  size={16}
                  className="text-state-warn-light dark:text-state-warn-dark"
                />{' '}
                {t('details.sections.systemHealth')}
              </h3>
              <p className="text-xs text-text-muted-light dark:text-text-muted-dark font-medium leading-relaxed">
                {t(
                  agent.status.toLowerCase() === 'online'
                    ? 'details.health.onlineMessage'
                    : agent.status.toLowerCase() === 'revoked'
                      ? 'details.health.revokedMessage'
                      : 'details.health.offlineMessage'
                )}
              </p>
            </Card>
          </div>
        </div>
      </div>

      {showCommunicationLogs && (
        <AgentCommunicationLogsViewer
          isOpen={showCommunicationLogs}
          onClose={() => setShowCommunicationLogs(false)}
          agentId={agent.id}
          agentName={agent.name}
        />
      )}

      {regeneratedToken && (
        <div className="fixed inset-0 z-[110] flex items-center justify-center p-4 bg-slate-900/90 backdrop-blur-md animate-in fade-in duration-500">
          <Card className="w-full max-w-lg p-10 text-center space-y-8 bg-surface-card-light border-none shadow-2xl">
            <div className="w-20 h-20 bg-state-warn-bg-light rounded-full flex items-center justify-center mx-auto text-state-warn-light">
              <ShieldCheck size={40} />
            </div>
            <div>
              <h2 className="text-3xl font-black text-slate-900 tracking-tight">
                {t('regenerate.newTokenTitle')}
              </h2>
              <p className="text-text-muted-light font-medium mt-2">
                {t('regenerate.newTokenDesc')}
              </p>
            </div>
            <div className="p-6 bg-surface-2-light rounded-2xl border-2 border-dashed border-surface-border-light group relative">
              <code className="text-xl font-black text-brand-600 break-all">
                {regeneratedToken.token}
              </code>
              <button
                onClick={async () => {
                  navigator.clipboard.writeText(regeneratedToken.token);
                  await showSuccess(t('wizard.tokenCopied'), t('wizard.tokenCopiedDesc'));
                }}
                className="absolute -top-3 -right-3 bg-surface-card-light shadow-md p-2 rounded-xl text-text-subtle-light hover:text-brand-600 transition-colors border border-surface-border-light"
              >
                <Copy size={16} />
              </button>
            </div>
            <div className="p-4 bg-state-warn-bg-light rounded-2xl flex items-start space-x-3 text-left">
              <AlertTriangle className="text-state-warn-light shrink-0" size={20} />
              <p className="text-xs text-state-warn-light font-bold leading-relaxed uppercase">
                {t('regenerate.warning')}
              </p>
            </div>
            <Button size="lg" className="w-full" onClick={() => setRegeneratedToken(null)}>
              {t('wizard.iveSavedToken')}
            </Button>
          </Card>
        </div>
      )}
    </>
  );
}

function DetailItem({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)] items-start gap-x-3 text-xs">
      <span className="min-w-0 break-words font-black leading-5 text-text-subtle-light dark:text-text-subtle-dark tracking-normal uppercase">
        {label}
      </span>
      <span
        className={`min-w-0 break-words text-right font-bold leading-5 text-slate-900 dark:text-slate-200 ${mono ? 'font-mono' : ''}`}
      >
        {value}
      </span>
    </div>
  );
}

function StatBox({ label, value, color }: { label: string; value: string; color: string }) {
  return (
    <div className="bg-surface-2-light dark:bg-surface-2-dark p-3 rounded-xl border border-surface-border-light dark:border-surface-border-dark">
      <p className="text-[10px] font-black text-text-subtle-light dark:text-text-subtle-dark uppercase tracking-widest mb-1">
        {label}
      </p>
      <p className={`text-lg font-black ${color}`}>{value}</p>
    </div>
  );
}
